import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
  Collapse,
  Grid,
} from '@mui/material';

import {
  ExpandMore as ExpandMoreIcon,
  WhatsApp as WhatsAppIcon,
  PhoneIphoneRounded,
  PersonRounded,
  PhoneRounded,
  EmailRounded,
  EventOutlined,
} from '@mui/icons-material';

import { Creators as TradeInSimulationCreators } from 'store/trade-in-simulations/duck.js';
import * as S from './styled';

// Helpers
const digitsOnly = (v) => String(v || '').replace(/\D/g, '');
const firstName = (full) =>
  (
    String(full || '')
      .trim()
      .split(' ')[0] || ''
  ).trim();
const toE164Brazil = (raw) => {
  const d = digitsOnly(raw);
  if (!d) return '';
  if (d.startsWith('55')) return d;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  return d;
};

const formatDateTime = (v) => {
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '-';
  }
};

const STATUS_LABELS = ['Solicitado', 'Atendido', 'Finalizado'];
const STATUS_MAP = {
  solicitado: 'Solicitado',
  atendido: 'Atendido',
  finalizado: 'Finalizado',
};

const normalizeStatusValue = (status) => {
  if (typeof status === 'number') return STATUS_LABELS[status] ?? '';
  if (typeof status === 'string') {
    const s = status.trim();
    // tenta match direto
    if (STATUS_LABELS.includes(s)) return s;
    // tenta case-insensitive
    const ci = STATUS_MAP[s.toLowerCase()];
    if (ci) return ci;
  }
  return '';
};

const statusColor = (statusNormalized) => {
  switch (statusNormalized) {
    case 'Solicitado':
      return '#ffdf5a'; // amarelo
    case 'Atendido':
      return '#92b21f'; // verde
    case 'Finalizado':
      return '#ea5339'; // cinza
    default:
      return '#444';
  }
};

// Acessa propriedades com tolerância de casing (camel/Pascal)
const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  return undefined;
};

// Mensagem dinâmica para WhatsApp (texto apenas, sem URL gerada antecipadamente)
const buildWhatsappMessage = (item) => {
  const fullName = pick(item, 'fullName', 'FullName');
  const device = pick(item, 'device', 'Device');
  const interested = pick(item, 'interestedIPhone', 'InterestedIPhone', 'interestedIphone');
  const capacity = pick(item, 'capacity', 'Capacity');
  const battery = pick(item, 'batteryHealth', 'BatteryHealth');
  const usage = pick(item, 'usageOrBroken', 'UsageOrBroken');
  const malfunc = pick(item, 'malfunctions', 'Malfunctions');

  const nomeCliente = firstName(fullName);
  const celularPretendido = interested ? ` por um ${interested}` : '';
  const head = `Olá ${nomeCliente || ''}, recebemos sua solicitação de simulação para troca do seu celular ${device || ' '}${celularPretendido}!`;

  const detalhes = [];
  if (capacity) detalhes.push(`${capacity}`);
  if (battery) detalhes.push(`saúde da bateria em ${battery}`);

  const avariasList = [];
  if (usage) avariasList.push(usage);
  if (malfunc) avariasList.push(malfunc);

  const partes = [head];
  if (detalhes.length) partes.push(`Entendemos que seu celular tem ${detalhes.join(', ')}.`);
  if (avariasList.length)
    partes.push(`Observamos as seguintes avarias/observações: ${avariasList.join(' | ')}.`);
  partes.push('Com base nisso, montamos uma proposta para que você troque seu celular.');

  return encodeURIComponent(partes.join(' '));
};

const ManagementHomePage = ({
  list,
  listLoading,
  tradeInSimulationsFetchAsync,
  tradeInSimulationsUpdateStatusAsync,
}) => {
  const [expanded, setExpanded] = useState({}); // { [id]: boolean }

  useEffect(() => {
    tradeInSimulationsFetchAsync();
  }, [tradeInSimulationsFetchAsync]);

  const handleToggle = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleStatusChange = useCallback(
    (id) => (e) => {
      const newStatus = e.target.value;
      tradeInSimulationsUpdateStatusAsync(id, newStatus);
    },
    [tradeInSimulationsUpdateStatusAsync],
  );

  // Gera a URL do WhatsApp SOMENTE no clique (melhor performance)
  const handleWhatsappClick = useCallback((item) => {
    const phone = pick(item, 'phoneNumber', 'PhoneNumber');
    const waNumber = toE164Brazil(phone);
    const text = buildWhatsappMessage(item);
    const url = waNumber ? `https://wa.me/${waNumber}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const cards = useMemo(() => (Array.isArray(list) ? list : []), [list]);

  return (
    <S.PageContainer>
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#111',
        }}
      >
        <PhoneIphoneRounded />
        Solicitações de Simulação
      </Typography>

      <Grid container spacing={2} columns={{ xs: 12, md: 12 }}>
        {cards.map((item) => {
          const id = pick(item, 'id', 'Id');
          const createdAt = pick(item, 'createdAt', 'CreatedAt');
          const fullName = pick(item, 'fullName', 'FullName');
          const phone = pick(item, 'phoneNumber', 'PhoneNumber');
          const email = pick(item, 'email', 'Email');
          const rawStatus = pick(item, 'status', 'Status');
          const statusNormalized = normalizeStatusValue(rawStatus);
          const border = statusColor(statusNormalized);
          // Detalhes (ficam SOMENTE no collapsible)
          const device = pick(item, 'device', 'Device');
          const interested = pick(item, 'interestedIPhone', 'InterestedIPhone', 'interestedIphone');
          const capacity = pick(item, 'capacity', 'Capacity');
          const color = pick(item, 'color', 'Color');
          const battery = pick(item, 'batteryHealth', 'BatteryHealth');
          const usage = pick(item, 'usageOrBroken', 'UsageOrBroken');
          const malfunc = pick(item, 'malfunctions', 'Malfunctions');
          const payment = pick(item, 'paymentMethod', 'PaymentMethod');

          return (
            <Grid key={id} size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  border: `2px solid ${border}`,
                  borderRadius: '16px',
                  bgcolor: '#ffffff',
                  color: '#111111',
                }}
              >
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonRounded fontSize="small" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111' }}>
                        {fullName || '-'}
                      </Typography>
                    </Stack>
                  }
                  subheader={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EventOutlined fontSize="inherit" />
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(createdAt)}
                      </Typography>
                    </Stack>
                  }
                  action={
                    <FormControl
                      fullWidth
                      margin="normal"
                      sx={{ minWidth: 180, mt: 0 /* zera a margem extra do header */ }}
                    >
                      <InputLabel id={`status-label-${id}`} shrink>
                        Status
                      </InputLabel>
                      <Select
                        id={`status-select-${id}`}
                        labelId={`status-label-${id}`}
                        label="Status"
                        value={statusNormalized}
                        onChange={handleStatusChange(id)}
                        notched
                      >
                        {STATUS_LABELS.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  }
                />

                {/* ESSENCIAIS no card: Telefone + E-mail */}
                <CardContent>
                  <Stack spacing={1.2}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <PhoneRounded fontSize="small" />
                      <Typography variant="body2" sx={{ color: '#111' }}>
                        {phone || '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <EmailRounded fontSize="small" />
                      <Typography variant="body2" sx={{ color: '#111' }}>
                        {email || '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                  {/* WhatsApp: sempre aparece; URL gerada sob demanda */}
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Abrir WhatsApp">
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<WhatsAppIcon />}
                          onClick={() => handleWhatsappClick(item)}
                        >
                          WhatsApp
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  <Tooltip title="Ver detalhes">
                    <IconButton onClick={() => handleToggle(id)}>
                      <ExpandMoreIcon
                        sx={{
                          transform: expanded[id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </CardActions>

                <Collapse in={!!expanded[id]} timeout="auto" unmountOnExit>
                  <CardContent sx={{ pt: 0 }}>
                    <S.DetailList>
                      <li>
                        <span>Celular informado:</span> {device || '-'}
                      </li>
                      <li>
                        <span>iPhone de interesse:</span> {interested || '-'}
                      </li>
                      <li>
                        <span>Capacidade:</span> {capacity || '-'}
                      </li>
                      <li>
                        <span>Cor:</span> {color || '-'}
                      </li>
                      <li>
                        <span>Saúde da bateria:</span> {battery || '-'}
                      </li>
                      <li>
                        <span>Marcas/Quebra:</span> {usage || '-'}
                      </li>
                      <li>
                        <span>Problemas relatados:</span> {malfunc || '-'}
                      </li>
                      <li>
                        <span>Método de pagamento:</span> {payment || '-'}
                      </li>
                    </S.DetailList>
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {!listLoading && cards.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Nenhuma solicitação encontrada.
        </Typography>
      )}
    </S.PageContainer>
  );
};

const mapStateToProps = (state) => ({
  list: state.TradeInSimulations?.list,
  listLoading: state.TradeInSimulations?.listLoading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(TradeInSimulationCreators, dispatch);

ManagementHomePage.propTypes = {};

export default connect(mapStateToProps, mapDispatchToProps)(ManagementHomePage);
